/**
 * Dialog zum Erzeugen druckfertiger Honorar-Dokumente (DOCX) für eine
 * Honorarkraft aus hochgeladenen Vorlagen. Zwei Modi:
 *   - Einzeldokument (ein DOCX)
 *   - Komplettpaket: mehrere Dokumente gebündelt als ein ZIP-Download
 */
import React, { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { FileDown, Loader2, Package } from 'lucide-react';
import { toast } from 'sonner';
import { honorarDocsAPI, massnahmenAPI } from '../../lib/api';

const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const b64ToBlob = (b64, mime) => {
  const bytes = atob(b64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new Blob([arr], { type: mime });
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const HonorarDocGenerateDialog = ({ open, onOpenChange, honorarkraft }) => {
  const [templates, setTemplates] = useState([]);
  const [massnahmen, setMassnahmen] = useState([]);
  const [bundleMode, setBundleMode] = useState(false);
  const [templateId, setTemplateId] = useState('');
  const [bundleIds, setBundleIds] = useState([]);
  const [massnahmeId, setMassnahmeId] = useState('');
  const [von, setVon] = useState('');
  const [bis, setBis] = useState('');
  const [funktion, setFunktion] = useState('');
  const [stunden, setStunden] = useState('');
  const [saveContract, setSaveContract] = useState(true);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const selectedTpl = templates.find((t) => t.id === templateId);
  const bundleSelected = templates.filter((t) => bundleIds.includes(t.id));
  // Beauftragungs-Felder anzeigen, wenn (Einzel) beauftragung ODER (Paket) eine beauftragung enthalten ist
  const isBeauftragung = bundleMode
    ? bundleSelected.some((t) => t.doc_type === 'beauftragung')
    : selectedTpl?.doc_type === 'beauftragung';
  const showSaveContract = bundleMode
    ? bundleSelected.some((t) => t.doc_type === 'rahmenvertrag' || t.doc_type === 'beauftragung')
    : (selectedTpl?.doc_type === 'rahmenvertrag' || isBeauftragung);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setBundleMode(false);
    setTemplateId('');
    setBundleIds([]);
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const iso = (d) => d.toISOString().slice(0, 10);
    setVon(iso(first)); setBis(iso(last));
    setFunktion((honorarkraft?.positions || [])[0] || '');
    Promise.all([honorarDocsAPI.listTemplates(), massnahmenAPI.getAllUnscoped()])
      .then(([t, m]) => { setTemplates(t.data); setMassnahmen(m.data || []); })
      .catch(() => toast.error('Vorlagen/Maßnahmen konnten nicht geladen werden'))
      .finally(() => setLoading(false));
  }, [open, honorarkraft]);

  const toggleBundle = (id) =>
    setBundleIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const commonPayload = () => ({
    massnahme_id: isBeauftragung ? massnahmeId : null,
    zeitraum_von: isBeauftragung ? von : null,
    zeitraum_bis: isBeauftragung ? bis : null,
    funktion: funktion || null,
    geplante_stunden: stunden ? parseFloat(stunden) : null,
    save_as_contract: saveContract,
  });

  const onGenerate = async () => {
    if (isBeauftragung && !massnahmeId) { toast.error('Bitte eine Maßnahme wählen'); return; }
    setGenerating(true);
    try {
      if (bundleMode) {
        if (bundleIds.length === 0) { toast.error('Bitte mindestens eine Vorlage wählen'); setGenerating(false); return; }
        const { data } = await honorarDocsAPI.generateBundle(honorarkraft.id, {
          template_ids: bundleIds, ...commonPayload(),
        });
        downloadBlob(b64ToBlob(data.content_base64, 'application/zip'), data.filename);
        toast.success(`${data.count} Dokumente als ZIP erstellt`);
      } else {
        if (!templateId) { toast.error('Bitte eine Vorlage wählen'); setGenerating(false); return; }
        const { data } = await honorarDocsAPI.generate(honorarkraft.id, {
          template_id: templateId, ...commonPayload(),
        });
        downloadBlob(b64ToBlob(data.content_base64, DOCX_MIME), data.filename);
        toast.success('Dokument erstellt');
      }
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Dokument konnte nicht erzeugt werden');
    } finally { setGenerating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="honorar-generate-dialog">
        <DialogHeader>
          <DialogTitle>Dokument erzeugen</DialogTitle>
          <DialogDescription>
            {honorarkraft ? `${honorarkraft.last_name}, ${honorarkraft.first_name}` : ''} — druckfertige Unterlagen aus Vorlagen
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
        ) : (
          <div className="space-y-4 py-1">
            <div className="flex items-center justify-between gap-3 rounded-md border p-3 bg-secondary/20">
              <div>
                <Label className="text-sm flex items-center gap-1.5"><Package className="h-4 w-4 text-primary" /> Komplettpaket (ZIP)</Label>
                <p className="text-xs text-muted-foreground">Vertrag + Zusatzdokumente in einem Download bündeln.</p>
              </div>
              <Switch checked={bundleMode} onCheckedChange={setBundleMode} data-testid="honorar-gen-bundle-toggle" />
            </div>

            {!bundleMode ? (
              <div className="space-y-1.5">
                <Label>Vorlage</Label>
                <Select value={templateId} onValueChange={setTemplateId}>
                  <SelectTrigger data-testid="honorar-gen-template"><SelectValue placeholder="Vorlage wählen…" /></SelectTrigger>
                  <SelectContent>
                    {templates.length === 0
                      ? <div className="px-2 py-3 text-sm text-muted-foreground text-center">Keine Vorlagen — bitte erst im Tab „Vorlagen" hochladen.</div>
                      : templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.doc_type_label} · {t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label>Vorlagen für das Paket</Label>
                {templates.length === 0 ? (
                  <div className="px-2 py-3 text-sm text-muted-foreground text-center border rounded-md">Keine Vorlagen — bitte erst im Tab „Vorlagen" hochladen.</div>
                ) : (
                  <div className="max-h-56 overflow-y-auto rounded-md border divide-y" data-testid="honorar-gen-bundle-list">
                    {templates.map((t) => (
                      <label key={t.id} className="flex items-center gap-3 p-2.5 cursor-pointer hover:bg-secondary/40" data-testid={`honorar-gen-bundle-item-${t.id}`}>
                        <Checkbox checked={bundleIds.includes(t.id)} onCheckedChange={() => toggleBundle(t.id)} />
                        <span className="text-sm">{t.doc_type_label} · {t.name}</span>
                      </label>
                    ))}
                  </div>
                )}
                {bundleIds.length > 0 && <p className="text-xs text-muted-foreground">{bundleIds.length} Dokument(e) ausgewählt</p>}
              </div>
            )}

            {isBeauftragung && (
              <>
                <div className="space-y-1.5">
                  <Label>Maßnahme</Label>
                  <Select value={massnahmeId} onValueChange={setMassnahmeId}>
                    <SelectTrigger data-testid="honorar-gen-massnahme"><SelectValue placeholder="Maßnahme wählen…" /></SelectTrigger>
                    <SelectContent>
                      {massnahmen.map((m) => <SelectItem key={m.id} value={m.id}>{m.short_code} – {m.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Zeitraum von</Label>
                    <Input type="date" value={von} onChange={(e) => setVon(e.target.value)} data-testid="honorar-gen-von" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Zeitraum bis</Label>
                    <Input type="date" value={bis} onChange={(e) => setBis(e.target.value)} data-testid="honorar-gen-bis" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Funktion</Label>
                    <Input value={funktion} onChange={(e) => setFunktion(e.target.value)} placeholder="z.B. Dozent" data-testid="honorar-gen-funktion" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Geplante Stunden</Label>
                    <Input type="number" value={stunden} onChange={(e) => setStunden(e.target.value)} placeholder="z.B. 40" data-testid="honorar-gen-stunden" />
                  </div>
                </div>
              </>
            )}

            {showSaveContract && (
              <div className="flex items-center justify-between gap-3 rounded-md border p-3">
                <div>
                  <Label className="text-sm">Als Vertrag am Datensatz vermerken</Label>
                  <p className="text-xs text-muted-foreground">Ermöglicht Ablauf-Erinnerungen für Beauftragungen.</p>
                </div>
                <Switch checked={saveContract} onCheckedChange={setSaveContract} data-testid="honorar-gen-save" />
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={() => onOpenChange(false)} disabled={generating}>Abbrechen</Button>
          <Button className="rounded-full" onClick={onGenerate} disabled={generating || loading} data-testid="honorar-gen-submit">
            {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : (bundleMode ? <Package className="h-4 w-4 mr-2" /> : <FileDown className="h-4 w-4 mr-2" />)}
            {bundleMode ? 'Paket erzeugen (ZIP)' : 'Erzeugen & herunterladen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
